import React, { useRef, useEffect } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { MicOff, VideoOff } from 'lucide-react';

interface VideoTileProps {
  stream: MediaStream | null;
  name: string;
  avatarUrl?: string;
  isLocal?: boolean;
  isMuted?: boolean;
  isVideoOff?: boolean;
  className?: string;
}

export function VideoTile({
  stream,
  name,
  avatarUrl,
  isLocal = false,
  isMuted = false,
  isVideoOff = false,
  className = '',
}: VideoTileProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Bind stream to video element (for video tracks)
  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  // Always bind stream to a hidden audio element for remote audio playback.
  // This ensures audio plays even when the video element is not rendered (audio-only calls).
  useEffect(() => {
    if (!isLocal && audioRef.current && stream) {
      audioRef.current.srcObject = stream;
      audioRef.current.play().catch(() => {
        // Autoplay may be blocked - user interaction usually already happened via accept button
      });
    }
  }, [stream, isLocal]);

  const hasVideo = stream && stream.getVideoTracks().length > 0 && !isVideoOff;
  const initials = name
    .split(' ')
    .map(w => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className={`relative bg-gray-900 rounded-xl overflow-hidden flex items-center justify-center ${className}`}>
      {/* Hidden audio element - always present for remote streams to ensure audio plays */}
      {!isLocal && stream && (
        <audio ref={audioRef} autoPlay playsInline className="hidden" />
      )}

      {hasVideo ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted={isLocal}
          className={`w-full h-full object-cover ${isLocal ? 'scale-x-[-1]' : ''}`}
        />
      ) : (
        <div className="flex flex-col items-center justify-center gap-2">
          <Avatar className="h-20 w-20">
            <AvatarImage src={avatarUrl} />
            <AvatarFallback className="text-2xl bg-gray-700 text-white">
              {initials}
            </AvatarFallback>
          </Avatar>
          <span className="text-white text-sm font-medium">{name}</span>
        </div>
      )}

      {/* Name label */}
      <div className="absolute bottom-2 left-2 flex items-center gap-1.5 bg-black/50 rounded-md px-2 py-1">
        <span className="text-white text-xs font-medium truncate max-w-[120px]">
          {isLocal ? 'You' : name}
        </span>
        {isMuted && <MicOff className="h-3 w-3 text-red-400" />}
        {isVideoOff && <VideoOff className="h-3 w-3 text-red-400" />}
      </div>
    </div>
  );
}
